<template>
  <div class="cluster-entity-selector">
    <!-- Filters Row -->
    <v-row class="mb-3" dense>
      <!-- Government Type Filter -->
      <v-col cols="6" class="pa-0 ma-0 d-flex align-start">
        <v-combobox
          v-model="selectedGovTypes"
          :items="gov_type_options"
          item-text="text"
          item-value="value"
          return-object
          x-small-chips
          multiple
          chips
          deletable-chips
          :rounded="false"
          :solo="true"
          clearable
          dense
          outlined
          label="Government"
          x-small
          :menu-props="{ maxWidth: '400', maxHeight: '400' }"
          class="pr-2 gov-combobox flex-grow-1"
          style="min-height: 40px;"
          @click:clear="clearGovTypeFilter()"
          @change="applyFilters"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.text || item.value || item }}</v-list-item-title>
            </v-list-item-content>
          </template>
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              v-if="item"
              color="primary"
              label
              small
              v-bind="attrs"
              :input-value="selected"
              close
              @click="select"
              @click:close="removeGovType(item)"
              class="mr-1"
            >
              {{ item.text || item.value || item }}
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
      <!-- COFOG Classification Filter -->
      <v-col cols="6" class="pa-0 ma-0 d-flex align-start">
        <v-combobox
          v-model="selectedCofogs"
          :items="cofog_options_formatted"
          item-text="text"
          item-value="value"
          return-object
          :loading="loadingCofogs"
          x-small-chips
          multiple
          chips
          deletable-chips
          :rounded="false"
          :solo="true"
          clearable
          dense
          outlined
          label="Industry Classification"
          x-small
          :menu-props="{ maxWidth: '400', maxHeight: '400' }"
          class="pr-2 cofog-combobox flex-grow-1"
          style="min-height: 40px;"
          @click:clear="clearCofogFilter()"
          @change="applyFilters"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title :style="{ paddingLeft: (item.level - 1) * 20 + 'px' }">
                {{ item.text }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
          <template v-slot:selection="{ attrs, item, select, selected }">
            <v-chip
              color="primary"
              label
              small
              v-bind="attrs"
              :input-value="selected"
              close
              @click="select"
              @click:close="removeCofog(item)"
              class="mr-1"
            >
              {{ item.text }}
            </v-chip>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    
    <!-- Search Bar -->
    <v-combobox
      v-model="term_chips"
      :search-input.sync="keyword_search"
      small-chips
      clearable
      outlined
      dense
      label="Enter buyer keywords to search. Use <tab> or <enter> after each keyword to seperate them."
      multiple
      small
      ref="keywords"
      append-outer-icon="mdi-magnify"
      class="mb-4"
      @click:append-outer="performSearch"
      @click:clear="clearSearch"
      @keydown.enter.prevent="handleKeywordEnter"
      @keydown.tab.prevent="handleKeywordTab"
    >
      <template v-slot:selection="{ attrs, item, select, selected }">
        <v-chip
          color="primary"
          small
          label
          v-bind="attrs"
          :input-value="selected"
          close
          @click="select"
          @click:close="removeKeyword(item)"
        >{{ item }}</v-chip>
      </template>
    </v-combobox>
    
    <!-- Select All Button -->
    <div v-if="entities.length > 0" class="mb-3">
      <v-btn
        small
        outlined
        @click="toggleSelectAll"
        :disabled="loading"
      >
        <v-icon left>{{ allSelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
        {{ allSelected ? 'Deselect All' : 'Select All' }}
      </v-btn>
      <span class="ml-2 caption grey--text">
        {{ entities.length.toLocaleString() }}{{ totalResults > 0 ? ` of ${totalResults.toLocaleString()}` : '' }} buyers
      </span>
    </div>
    
    <!-- Loading State -->
    <v-progress-linear v-if="loading" indeterminate class="mb-4" />
    
    <!-- Entity List -->
    <div v-if="!loading" class="entity-list" style="max-height: 400px; overflow-y: auto;" @scroll="onScroll">
      <v-list dense>
        <v-list-item
          v-for="entity in displayedEntities"
          :key="entity.id"
          @click="toggleSelection(entity)"
          :class="{ 'selected': isSelected(entity.id) }"
          class="entity-item"
        >
          <v-list-item-content>
            <v-list-item-title>{{ getEntityName(entity) }}</v-list-item-title>
            <v-list-item-subtitle v-if="getEntityOrganisation(entity)">
              {{ getEntityOrganisation(entity) }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action>
            <v-icon v-if="isSelected(entity.id)" color="primary">mdi-check</v-icon>
            <v-icon v-else color="grey lighten-1">mdi-circle-outline</v-icon>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      
      <!-- Load More Button -->
      <div v-if="hasMoreResults && !loadingMore" class="text-center pa-2">
        <v-btn small outlined @click="loadMoreResults">
          Load More
        </v-btn>
      </div>
      
      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="text-center pa-2">
        <v-progress-circular size="20" indeterminate></v-progress-circular>
      </div>
      
      <!-- No results message -->
      <div v-if="displayedEntities.length === 0 && !loading" class="text-center pa-4">
        <v-icon size="48" color="grey lighten-1">mdi-magnify</v-icon>
        <p class="grey--text mt-2">No buyers found</p>
        <p class="caption grey--text">Try adjusting your search terms</p>
      </div>
    </div>
    
    <!-- Selected count and Add Button -->
    <div v-if="selectedEntities.length > 0" class="mt-4">
      <v-alert type="info" dense class="mb-3">
        {{ selectedEntities.length }} buyer{{ selectedEntities.length > 1 ? 's' : '' }} selected
      </v-alert>
      
      <v-btn
        color="primary"
        block
        :loading="adding"
        @click="addToCluster"
      >
        Add {{ selectedEntities.length }} to Cluster
      </v-btn>
    </div>
  </div>
</template>

<script>
import { searchBuyers, updateClusters, fetchGovTypes, fetchCofogClassifications } from "./APIManager.js";
import { getInstance } from "../auth";

export default {
  name: "ClusterEntitySelector",
  props: {
    clusterId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      term_chips: [],
      keyword_search: '',
      entities: [],
      selectedEntities: [],
      loading: false,
      adding: false,
      currentPage: 1,
      pageSize: 300,
      hasMoreResults: false,
      loadingMore: false,
      totalResults: 0,
      selectedGovTypes: [],
      gov_type_options: [],
      selectedCofogs: [],
      cofog_options: [],
      cofog_options_formatted: [],
      loadingCofogs: false
    };
  },
  computed: {
    displayedEntities() {
      return this.entities;
    },
    
    allSelected() {
      return this.entities.length > 0 && this.selectedEntities.length === this.entities.length;
    },
    
    // Format selected gov types for display
    formattedSelectedGovTypes() {
      return this.selectedGovTypes.map(g => {
        if (typeof g === 'string') return g;
        if (g && typeof g === 'object' && g.text) return g.text;
        return '';
      }).filter(Boolean);
    }
  },
  mounted() {
    this.loadFilterOptions();
    this.loadEntities();
  },
  methods: {
    async loadFilterOptions() {
      // Load government types
      const instance = getInstance();
      if (instance.isAuthenticated) {
        try {
          const results = await fetchGovTypes();
          if (results && results.gov_types) {
            // Ensure we create proper objects with text and value properties
            this.gov_type_options = results.gov_types.map(gov => {
              // Handle both string and object inputs
              if (typeof gov === 'string') {
                return { text: gov, value: gov };
              } else if (gov && typeof gov === 'object') {
                return {
                  text: gov.text || gov.value || gov.name || String(gov),
                  value: gov.value || gov.text || gov.name || String(gov)
                };
              }
              return { text: String(gov), value: String(gov) };
            });
            //console.log('Loaded gov_type_options:', this.gov_type_options);
          }
        } catch (error) {
          //console.error('Error loading government types:', error);
        }
      }
      
      // Load COFOG classifications
      this.loadingCofogs = true;
      try {
        const response = await fetchCofogClassifications();
        if (response.status === 'success' && response.data) {
          this.cofog_options = response.data;
          this.formatCofogOptions();
        }
      } catch (error) {
        //console.error('Error loading COFOG classifications:', error);
      } finally {
        this.loadingCofogs = false;
      }
    },
    
    async loadEntities() {
      this.loading = true;
      this.currentPage = 1;
      this.entities = [];
      this.selectedEntities = [];
      
      try {
        // Use searchBuyers for initial load - join chips with commas for multiple terms
        const searchText = this.term_chips.length > 0 ? this.term_chips.join(',') : '';
        
        // Build filters object
        const filters = {};
        if (this.selectedGovTypes && this.selectedGovTypes.length > 0) {
          filters.gov_type = this.selectedGovTypes.map(g => g.value);
        }
        if (this.selectedCofogs && this.selectedCofogs.length > 0) {
          // Extract COFOG codes from selected items
          filters.cofog_code = this.selectedCofogs.map(c => c.value);
        }
        
        const response = await searchBuyers(searchText, this.currentPage, this.pageSize, filters);
        
        // Log response structure for debugging
        if (response.data && response.data.length > 0) {
          //console.log('First entity structure:', response.data[0]);
        }
        
        this.entities = response.data || [];
        this.totalResults = response.total || 0;
        this.hasMoreResults = response.has_more || false;
        
      } catch (error) {
        //console.error('Error loading entities:', error);
        this.$emit('error', 'Failed to load buyers');
      } finally {
        this.loading = false;
      }
    },
    
    performSearch() {
      this.loadEntities();
    },
    
    handleKeywordEnter() {
      // Add the current search input as a chip if it has a value
      if (this.keyword_search && this.keyword_search.trim()) {
        const trimmedValue = this.keyword_search.trim();
        if (!this.term_chips.includes(trimmedValue)) {
          this.term_chips.push(trimmedValue);
        }
        this.keyword_search = '';
      }
      // Trigger search after adding the chip
      this.$nextTick(() => {
        this.performSearch();
      });
    },
    
    handleKeywordTab() {
      // Add the current search input as a chip if it has a value
      if (this.keyword_search && this.keyword_search.trim()) {
        const trimmedValue = this.keyword_search.trim();
        if (!this.term_chips.includes(trimmedValue)) {
          this.term_chips.push(trimmedValue);
        }
        this.keyword_search = '';
      }
      // Trigger search after adding the chip
      this.$nextTick(() => {
        this.performSearch();
      });
    },
    
    clearSearch() {
      this.term_chips = [];
      this.loadEntities();
    },
    
    removeKeyword(item) {
      this.term_chips.splice(this.term_chips.indexOf(item), 1);
      this.term_chips = [...this.term_chips];
      if (this.term_chips.length === 0) {
        this.clearSearch();
      } else {
        this.performSearch();
      }
    },
    
    async loadMoreResults() {
      if (this.loadingMore || !this.hasMoreResults) return;
      
      this.loadingMore = true;
      this.currentPage++;
      
      try {
        const searchText = this.term_chips.length > 0 ? this.term_chips.join(',') : '';
        
        // Build filters object
        const filters = {};
        if (this.selectedGovTypes && this.selectedGovTypes.length > 0) {
          filters.gov_type = this.selectedGovTypes.map(g => g.value);
        }
        if (this.selectedCofogs && this.selectedCofogs.length > 0) {
          // Extract COFOG codes from selected items
          filters.cofog_code = this.selectedCofogs.map(c => c.value);
        }
        
        const response = await searchBuyers(searchText, this.currentPage, this.pageSize, filters);
        
        const newEntities = response.data || [];
        const wasAllSelected = this.selectedEntities.length === this.entities.length;
        this.entities = [...this.entities, ...newEntities];
        this.hasMoreResults = response.has_more || false;
        if (wasAllSelected && newEntities.length > 0) {
          this.selectedEntities = [...this.selectedEntities, ...newEntities];
        }
        
      } catch (error) {
        //console.error('Error loading more entities:', error);
        this.$emit('error', 'Failed to load more buyers');
        this.currentPage--; // Revert page on error
      } finally {
        this.loadingMore = false;
      }
    },
    
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const threshold = 50;
      
      if (scrollHeight - scrollTop - clientHeight < threshold && this.hasMoreResults && !this.loadingMore) {
        this.loadMoreResults();
      }
    },
    
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedEntities = [];
      } else {
        this.selectedEntities = [...this.entities];
      }
    },
    
    toggleSelection(entity) {
      const index = this.selectedEntities.findIndex(e => e.id === entity.id);
      if (index > -1) {
        this.selectedEntities.splice(index, 1);
      } else {
        this.selectedEntities.push(entity);
      }
    },
    
    isSelected(entityId) {
      return this.selectedEntities.some(e => e.id === entityId);
    },
    
    getEntityName(entity) {
      // Handle different possible property names for entity name
      if (!entity) return 'Unknown Entity';
      if (typeof entity === 'string') return entity;
      if (typeof entity.name === 'string') return entity.name;
      // If name exists but is not a string, it might be an object - try to extract a value
      if (entity.name) {
        if (typeof entity.name === 'object') {
          // Try common properties that might contain the name
          return entity.name.name || entity.name.value || entity.name.text || JSON.stringify(entity.name);
        }
      }
      // Try other common property names
      if (entity.title) return entity.title;
      if (entity.label) return entity.label;
      // Fallback to id or a default
      return entity.id ? `Entity ${entity.id}` : 'Unknown Entity';
    },
    
    getEntityOrganisation(entity) {
      if (!entity) return null;
      return entity.organisation || entity.organization || null;
    },
    
    formatCofogOptions() {
      // Recursively format COFOG options with hierarchical indentation
      const formatCofog = (cofogList, level = 1) => {
        const items = [];
        cofogList.forEach(cofog => {
          const indent = '  '.repeat(level - 1);
          items.push({
            text: `${indent}${cofog.code} - ${cofog.name}`,
            value: cofog.code,
            level: cofog.level || level
          });
          
          // Recursively process children
          if (cofog.children && cofog.children.length > 0) {
            items.push(...formatCofog(cofog.children, level + 1));
          }
        });
        return items;
      };

      this.cofog_options_formatted = formatCofog(this.cofog_options);
    },
    
    removeGovType(item) {
      const index = this.selectedGovTypes.findIndex(g => g.value === item.value);
      if (index > -1) {
        this.selectedGovTypes.splice(index, 1);
        this.applyFilters();
      }
    },
    
    clearGovTypeFilter() {
      this.selectedGovTypes = [];
      this.applyFilters();
    },
    
    removeCofog(item) {
      const index = this.selectedCofogs.findIndex(c => c.value === item.value);
      if (index > -1) {
        this.selectedCofogs.splice(index, 1);
        this.applyFilters();
      }
    },
    
    clearCofogFilter() {
      this.selectedCofogs = [];
      this.applyFilters();
    },
    
    applyFilters() {
      // Reload entities with new filters
      this.loadEntities();
    },
    
    async addToCluster() {
      if (this.selectedEntities.length === 0) return;
      
      this.adding = true;
      try {
        const entityIds = this.selectedEntities.map(e => e.id);
        await updateClusters([this.clusterId], 'publisher', entityIds);
        
        this.$emit('entities-added', this.selectedEntities);
        this.$emit('success', `Added ${this.selectedEntities.length} buyer${this.selectedEntities.length > 1 ? 's' : ''} to cluster`);
        
        // Clear selection
        this.selectedEntities = [];
        
      } catch (error) {
        //console.error('Error adding entities to cluster:', error);
        this.$emit('error', 'Failed to add buyers to cluster');
      } finally {
        this.adding = false;
      }
    }
  }
};
</script>

<style scoped>
.entity-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.entity-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.entity-item.selected {
  background-color: rgba(25, 118, 210, 0.08);
}

.entity-list {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.gov-combobox >>> .v-input__control,
.cofog-combobox >>> .v-input__control {
  min-height: 40px;
}

.gov-combobox >>> .v-input__slot,
.cofog-combobox >>> .v-input__slot {
  min-height: 40px;
  align-items: flex-start;
  padding-top: 4px;
}
</style>
